import {
  ChangeDetectorRef,
  Component,
  ElementRef,
  Input,
  OnChanges,
  OnInit,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';
import {
  slideToggle,
  slideToggleLocation,
} from '../../../../helpers/slidetoggle';
import { CimClass, EMCType, EntityModelCustomizationService, SubstationTemplateUICustomizationDto } from 'shared-lib';
import {
  EmcDataIn,
  EmcOptions,
  MetaDataItemDataIn,
} from '../../../../models/entity-model-customization.model';
import { ToastrService } from 'ngx-toastr';
import { Purpose } from '../../../../enums/purpose';
import { UtilitySharedService } from '../../../../shared/_services/utility-shared.service';

@Component({
  selector: 'app-emc-asset',
  templateUrl: './emc-asset.component.html',
  styleUrl: './emc-asset.component.scss',
  animations: [slideToggle, slideToggleLocation],
  standalone: false,
})
export class EmcAssetComponent implements OnChanges, OnInit {
  @ViewChild('closeEmcModal') closeEmcModal?: ElementRef;
  @Input() entityType?: EMCType;
  @Input() substationTemplate?: SubstationTemplateUICustomizationDto | null = null;
  @Input() selectedMainTab: string = '';
  @Input() cimClass?: CimClass;

  isListPageOpen: boolean = false;
  isDetailsPageOpen: boolean = false;
  isAdvanceMapOpen: boolean = false;
  utilityId: number = 0;
  title: string = '';

  allEmcOptions: Map<Purpose, EmcOptions[]> = new Map();
  selectedEmcOptions: Map<Purpose, EmcOptions[]> = new Map();

  filteredOptions: EmcOptions[] = [];
  tempSelectedOptions: EmcOptions[] = [];
  currentPurpose: Purpose = Purpose.Download;

  searchTerm: string = '';
  selectedEntityType: number;
  selectedSection: string = '';

  purposeEnum = Purpose;
  isPreviewOpen = false;

  get showDetailsPage(): boolean {
    return this.selectedMainTab == 'Assets' ||
      (this.selectedMainTab == 'Equipments' && this.cimClass == CimClass.PowerTransformer);
  }

  constructor(
    private cdr: ChangeDetectorRef,
    private emcService: EntityModelCustomizationService,
    private toastr: ToastrService,
    private utilitySharedService: UtilitySharedService
  ) {
    this.utilityId = this.utilitySharedService.getUtilityId()!;
  }

  ngOnInit(): void {
    if(this.selectedMainTab == 'Assets') {
      this.selectedEntityType = EMCType.TransmissionStation;
      this.title = 'ENTITY_MODEL_CUSTOMIZATION.TS';
    }
    else if(this.selectedMainTab == 'Equipments') {
      this.selectedEntityType = EMCType.EquipmentTemplate;
      this.title = 'ENTITY_MODEL_CUSTOMIZATION.EQUIPMENTS.POWER_TRANSFORMER';
    }
  }

  ngOnChanges(changes: SimpleChanges): void {    
    if(this.selectedMainTab == 'Assets') {
      if (changes['entityType']) {
        this.handleModelChange();
        this.loadAllData();
      }

      if (
        changes['substationTemplate'] &&
        this.entityType === EMCType.SubstationTemplate
      ) {
        this.handleModelChange();
        this.title = this.substationTemplate!.className;
        this.loadAllData();
        this.cdr.detectChanges();
      }
    }
    else if(this.selectedMainTab == 'Equipments') {
      if (changes['entityType']) {
        this.handleModelChange();
        this.loadAllData();
      }
      if (
        changes['cimClass'] &&
        this.entityType === EMCType.EquipmentTemplate
      ) {
        this.handleModelChange();
        this.loadAllData();
        this.cdr.detectChanges();
      }
    }
  }

  loadAllData() {
    this.selectedEmcOptions.clear();
    this.allEmcOptions.clear();

    [
      Purpose.Download,
      Purpose.Filter,
      Purpose.ListPage,
      Purpose.Search,
      Purpose.Edit,
      Purpose.Card,
      Purpose.AdvancedMapImage,
      Purpose.AdvancedMapAdditionalFields
    ].forEach((purpose) => {
      this.getEmcOptionsForPurpose(purpose);
      this.getSelectedEmcOptionsForPurpose(purpose);
    });
  }

  getEmcOptionsForPurpose(purpose: Purpose) {
    this.emcService
      .getEmcOptions(
        this.selectedEntityType,
        purpose,
        this.utilityId,
        this.substationTemplate?.id ?? undefined,
        this.cimClass ?? undefined
      )
      .subscribe({
        next: (response: any) => {
          const responseData = response.body;
          if (responseData?.status === 200 || responseData?.status === 'OK') {
            this.allEmcOptions.set(purpose, responseData.data || []);
            this.syncSelectionStateForPurpose(purpose);
          } else {
            this.toastr.error(
              responseData?.message || 'Failed to fetch EMC options'
            );
            this.allEmcOptions.set(purpose, []);
          }
        },
        error: () => {
          this.toastr.error('Error occurred while fetching EMC options');
        },
      });
  }

  getSelectedEmcOptionsForPurpose(purpose: Purpose) {

    this.emcService
      .getSelectedEmcOptions(
        this.selectedEntityType,
        purpose,
        this.utilityId,
        this.substationTemplate?.id ?? undefined,
        this.cimClass ?? undefined
      )
      .subscribe({
        next: (response: any) => {
          const responseData = response.body;
          if (responseData?.status === 200 || responseData?.status === 'OK') {
            this.selectedEmcOptions.set(purpose, responseData.data || []);

            const selected = this.selectedEmcOptions.get(purpose) || [];
            const options = this.allEmcOptions.get(purpose) || [];
            selected.forEach((sel) => {
              const opt = options.find(
                (o) =>
                  o.attributeDefinitionId === sel.attributeDefinitionId &&
                  o.labelName === sel.labelName
              );
              if (opt && sel.additionalStatsInfo) {
                opt.additionalStatsInfo = { ...sel.additionalStatsInfo };
              }
            });

            this.allEmcOptions.set(purpose, [...options]);

            this.syncSelectionStateForPurpose(purpose);
          } else {
            this.toastr.error(
              responseData?.message || 'Failed to fetch selected EMC options'
            );
            this.selectedEmcOptions.set(purpose, []);
          }
        },
        error: () => {
          this.toastr.error(
            'Error occurred while fetching selected EMC options'
          );
        },
      });
  }

  syncSelectionStateForPurpose(purpose: Purpose) {
    const options = this.allEmcOptions.get(purpose);
    const selected = this.selectedEmcOptions.get(purpose);

    if (
      !options ||
      !selected ||
      options.length === 0 ||
      selected.length === 0
    ) {
      return;
    }

    options.forEach((option) => {
      const selectedOption = selected.find(
        (sel) =>
          sel.attributeDefinitionId === option.attributeDefinitionId &&
          sel.labelName === option.labelName
      );

      if (selectedOption) {
        option.isSelected = true;
        option.displayOrder = selectedOption.displayOrder;
      } else {
        option.isSelected = false;
        option.displayOrder = 0;
      }
    });

    this.allEmcOptions.set(purpose, [...options]);
  }

  selectSection(section: string, purpose: Purpose) {
    this.selectedSection = section;
    this.currentPurpose = purpose;
    this.searchTerm = '';

    const options = this.allEmcOptions.get(purpose) || [];
    this.filteredOptions = [...options];

    const selected = this.selectedEmcOptions.get(purpose) || [];
    this.tempSelectedOptions = selected.map((opt) => ({ ...opt }));
  }

  filterOptions() {
    const term = this.searchTerm.toLowerCase().trim();
    const currentOptions = this.allEmcOptions.get(this.currentPurpose) || [];

    if (!term) {
      this.filteredOptions = [...currentOptions];
    } else {
      this.filteredOptions = currentOptions.filter((option) =>
        option.labelName.toLowerCase().includes(term)
      );
    }
  }

  toggleSelection(option: EmcOptions) {
    const currentOptions = this.allEmcOptions.get(this.currentPurpose) || [];
    const originalOption = currentOptions.find(
      (opt) =>
        opt.attributeDefinitionId === option.attributeDefinitionId &&
        opt.labelName === option.labelName
    );

    if (originalOption) {
      
      if (
        this.currentPurpose === Purpose.AdvancedMapImage &&
        !originalOption.isSelected &&
        this.getSelectedOptionsForCurrentPurpose().length >= 1
      ) {
        this.toastr.warning('You can select only one option for Image');
        return;
      }

      originalOption.isSelected = !originalOption.isSelected;

      if (originalOption.isSelected) {
        const maxOrder = this.getSelectedOptionsForCurrentPurpose().length;
        originalOption.displayOrder = maxOrder + 1;
      } else {
        originalOption.displayOrder = 0;
      }
    }

    const filteredOption = this.filteredOptions.find(
      (opt) =>
        opt.attributeDefinitionId === option.attributeDefinitionId &&
        opt.labelName === option.labelName
    );

    if (filteredOption && originalOption) {
      filteredOption.isSelected = originalOption.isSelected;
      filteredOption.displayOrder = originalOption.displayOrder;
    }

    this.updateTempSelectedOptions();
  }

  updateTempSelectedOptions() {
    const currentOptions = this.allEmcOptions.get(this.currentPurpose) || [];
    this.tempSelectedOptions = currentOptions
      .filter((opt) => opt.isSelected)
      .map((opt) => ({ ...opt }))
      .sort((a, b) => a.displayOrder - b.displayOrder);
  }

  isSelected(option: EmcOptions): boolean {
    return option.isSelected;
  }

  getSelectedOptionsForCurrentPurpose(): EmcOptions[] {
    const currentOptions = this.allEmcOptions.get(this.currentPurpose) || [];
    return currentOptions
      .filter((opt) => opt.isSelected)
      .sort((a, b) => a.displayOrder - b.displayOrder);
  }

  getSelectedOptionsForPurpose(purpose: Purpose): EmcOptions[] {
    const currentOptions = this.allEmcOptions.get(purpose) || [];
    return currentOptions
      .filter((opt) => opt.isSelected)
      .sort((a, b) => a.displayOrder - b.displayOrder);
  }

  saveSelection() {
    const currentOptions = this.allEmcOptions.get(this.currentPurpose) || [];
    const selectedOptions = currentOptions
      .filter((opt) => opt.isSelected)
      .sort((a, b) => a.displayOrder - b.displayOrder);

    selectedOptions.forEach((option, index) => {
      option.displayOrder = index + 1;
    });

    const metaDataItems: MetaDataItemDataIn[] = selectedOptions.map(
      (option) => {
        const item = new MetaDataItemDataIn();

        item.attributeDefinitionId = option.attributeDefinitionId || null;
        item.labelName = option.labelName;
        item.displayOrder = option.displayOrder;
        item.staticPropertyName = option.attributeDefinitionId
          ? null
          : option.staticPropertyName || null;
        item.purpose = this.currentPurpose;
        item.additionalStatsInfo = option.additionalStatsInfo
          ? { ...option.additionalStatsInfo }
          : null;

        return item;
      }
    );

    const dataIn = new EmcDataIn();
    dataIn.entityType = this.selectedEntityType;
    dataIn.utilityId = this.utilityId;
    dataIn.purpose = this.currentPurpose;
    dataIn.metaDataItems = metaDataItems;
    dataIn.substationTemplateId = this.substationTemplate?.id ?? undefined;
    dataIn.cimClass = this.cimClass;

    this.saveEmcData(dataIn);
  }

  saveEmcData(dataIn: EmcDataIn) {
    this.emcService.saveEmcOptions(dataIn).subscribe({
      next: (response: any) => {
        const responseData = response.body;
        if (responseData?.status === 200 || responseData?.status === 'OK') {
          this.toastr.success(
            responseData?.message || 'EMC data saved successfully'
          );
          this.getSelectedEmcOptionsForPurpose(this.currentPurpose);
          this.closeModal();
        } else {
          this.toastr.error(responseData?.message || 'Failed to save EMC data');
        }
      },
      error: () => {
        this.toastr.error('Error occurred while saving EMC data');
      },
    });
  }

  closeModal() {
    this.closeEmcModal?.nativeElement.click();
  }

  onSelectedCardDrop(event: CdkDragDrop<EmcOptions[]>) {
    moveItemInArray(
      this.tempSelectedOptions,
      event.previousIndex,
      event.currentIndex
    );

    this.tempSelectedOptions.forEach((option, index) => {
      option.displayOrder = index + 1;

      const currentOptions = this.allEmcOptions.get(this.currentPurpose) || [];
      const originalOption = currentOptions.find(
        (opt) =>
          opt.attributeDefinitionId === option.attributeDefinitionId &&
          opt.labelName === option.labelName
      );
      if (originalOption) {
        originalOption.displayOrder = index + 1;
      }
    });
  }

  handleModelChange() {
    switch (this.entityType) {
      case EMCType.TransmissionStation:
        this.title = 'ENTITY_MODEL_CUSTOMIZATION.TS';
        this.selectedEntityType = EMCType.TransmissionStation;
        break;
      case EMCType.InjectionSubstation:
        this.title = 'ENTITY_MODEL_CUSTOMIZATION.IS';
        this.selectedEntityType = EMCType.InjectionSubstation;
        break;
      case EMCType.DistributionSubstation:
        this.title = 'ENTITY_MODEL_CUSTOMIZATION.DS';
        this.selectedEntityType = EMCType.DistributionSubstation;
        break;
      case EMCType.MainFeeder:
        this.title = 'ENTITY_MODEL_CUSTOMIZATION.MAIN_FEEDER';
        this.selectedEntityType = EMCType.MainFeeder;
        break;
      case EMCType.SecondaryFeeder:
        this.title = 'ENTITY_MODEL_CUSTOMIZATION.SECONDARY_FEEDER';
        this.selectedEntityType = EMCType.SecondaryFeeder;
        break;
      case EMCType.Pole:
        this.title = 'ENTITY_MODEL_CUSTOMIZATION.POLES';
        this.selectedEntityType = EMCType.Pole;
        break;
      case EMCType.Device:
        this.title = 'ENTITY_MODEL_CUSTOMIZATION.DEVICES';
        this.selectedEntityType = EMCType.Device;
        break;
      case EMCType.Building:
        this.title = 'ENTITY_MODEL_CUSTOMIZATION.BUILDINGS';
        this.selectedEntityType = EMCType.Building;
        break;
      case EMCType.Meter:
        this.title = 'ENTITY_MODEL_CUSTOMIZATION.METERS';
        this.selectedEntityType = EMCType.Meter;
        break;
      case EMCType.Customer:
        this.title = 'ENTITY_MODEL_CUSTOMIZATION.CUSTOMERS';
        this.selectedEntityType = EMCType.Customer;
        break;
      case EMCType.SubstationTemplate:
        this.title = this.substationTemplate!.className;
        this.selectedEntityType = EMCType.SubstationTemplate;
        break;
      case EMCType.EquipmentTemplate:
        this.title = this.setTitleByCimClass();
        this.selectedEntityType = EMCType.EquipmentTemplate;
        break;
      default:
        break;
    }
    this.cdr.detectChanges();
    this.isDetailsPageOpen = false;
    this.isListPageOpen = false;
    this.isAdvanceMapOpen = false;
  }

  setTitleByCimClass(): string {
    switch (this.cimClass) {

      case CimClass.PowerTransformer:
        return 'ENTITY_MODEL_CUSTOMIZATION.EQUIPMENTS.POWER_TRANSFORMER';

      case CimClass.AcLineSegment:
        return 'ENTITY_MODEL_CUSTOMIZATION.EQUIPMENTS.AC_LINE_SEGMENT';

      case CimClass.SwitchGear:
        return 'ENTITY_MODEL_CUSTOMIZATION.EQUIPMENTS.SWITCH_GEAR';

      case CimClass.Conductor:
        return 'ENTITY_MODEL_CUSTOMIZATION.EQUIPMENTS.CONDUCTOR';

      case CimClass.Busbar:
        return 'ENTITY_MODEL_CUSTOMIZATION.EQUIPMENTS.BUSBAR';

      case CimClass.Junction:
        return 'ENTITY_MODEL_CUSTOMIZATION.EQUIPMENTS.JUNCTION';

      case CimClass.Jumper:
        return 'ENTITY_MODEL_CUSTOMIZATION.EQUIPMENTS.JUMPER';

      case CimClass.Fuse:
        return 'ENTITY_MODEL_CUSTOMIZATION.EQUIPMENTS.FUSE';

      case CimClass.Disconnector:
        return 'ENTITY_MODEL_CUSTOMIZATION.EQUIPMENTS.DISCONNECTOR';

      case CimClass.GroundDisconnector:
        return 'ENTITY_MODEL_CUSTOMIZATION.EQUIPMENTS.GROUND_DISCONNECTOR';

      case CimClass.LoadBreakSwitch:
        return 'ENTITY_MODEL_CUSTOMIZATION.EQUIPMENTS.LOAD_BREAK_SWITCH';

      case CimClass.Breaker:
        return 'ENTITY_MODEL_CUSTOMIZATION.EQUIPMENTS.BREAKER';

      case CimClass.Ground:
        return 'ENTITY_MODEL_CUSTOMIZATION.EQUIPMENTS.GROUND';

      case CimClass.Recloser:
        return 'ENTITY_MODEL_CUSTOMIZATION.EQUIPMENTS.RECLOSER';

      case CimClass.Cut:
        return 'ENTITY_MODEL_CUSTOMIZATION.EQUIPMENTS.CUT';

      case CimClass.Sectionaliser:
        return 'ENTITY_MODEL_CUSTOMIZATION.EQUIPMENTS.SECTIONALISER';

      case CimClass.PetersenCoil:
        return 'ENTITY_MODEL_CUSTOMIZATION.EQUIPMENTS.PETERSEN_COIL';

      case CimClass.ShuntCompensator:
        return 'ENTITY_MODEL_CUSTOMIZATION.EQUIPMENTS.SHUNT_COMPENSATOR';

      case CimClass.FrequencyConverter:
        return 'ENTITY_MODEL_CUSTOMIZATION.EQUIPMENTS.FREQUENCY_CONVERTER';

      case CimClass.StaticVarCompensator:
        return 'ENTITY_MODEL_CUSTOMIZATION.EQUIPMENTS.STATIC_VAR_COMPENSATOR';

      default:
        return '';
    }
  }

  toggleListPageDropdown() {
    this.isListPageOpen = !this.isListPageOpen;
  }

  toggleDetailsPageDropdown() {
    this.isDetailsPageOpen = !this.isDetailsPageOpen;
  }

  toggleAdvancedMapDropdown() {
    this.isAdvanceMapOpen = !this.isAdvanceMapOpen;
  }

  openPreview() {
    this.isPreviewOpen = true;
  }

  closePreview() {
    this.isPreviewOpen = false;
  }
}
